import { collection, deleteDoc, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query, setDoc, updateDoc, where, Unsubscribe, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import { FireClient } from './FireClient';
import { AiTag, MemoCategory, MemoParagraph, MinutesSummary } from '../models/AiMemo';

// ============================
// Firestore Collection Structure:
//   rooms/{roomId}/ai-memos/{memoId}
//   rooms/{roomId}/ai-minutes/{minutesId}
//
// 현재: roomId = 유저의 personal room (uid)
// 향후: 특정 Room 내부로 이동 가능
// ============================

export interface AiMemoDoc {
    id: string;
    roomId: string;
    userId: string;
    title: string;
    content: string;                   // full text content
    paragraphs: MemoParagraph[];
    category: string;                  // category ID
    tags: AiTag[];
    summary?: string;
    language?: string;
    duration?: number;
    status: 'draft' | 'saved' | 'archived';
    created: any;                     // serverTimestamp
    updated: any;                     // serverTimestamp
}

export interface AiMinutesDoc {
    id: string;
    roomId: string;
    userId: string;
    title: string;
    transcript: string;                // raw transcript text
    minutes?: MinutesSummary;          // AI-summarized minutes
    tags: AiTag[];
    icon?: string;                     // AI-selected emoji icon
    language?: string;
    duration: number;
    attendees?: string[];              // 수동 입력 참석자 목록
    status: 'recording' | 'processing' | 'completed';
    sttStatus?: 'processing' | 'completed' | 'failed';
    sttError?: string;
    source?: 'itp-call';               // 통화 링크 회의록 — AI회의록 목록에서 제외, 통화 링크 메뉴에 표시
    itpLinkId?: string;                // 생성 원본 통화 링크 (seed-* = dev 시드)
    created: any;
    updated: any;
}

const MEMO_COL = 'ai-memos';
const MINUTES_COL = 'ai-minutes';
const ROOM_COL = 'rooms';

export class FireClientAiMemo {
    constructor(private c: FireClient) { }

    // ============================== Memos ==============================

    /**
     * Subscribe to room's memos (real-time)
     * @param roomId - 현재는 personal room (uid), 향후 특정 room
     */
    subscribeToMemos(roomId: string, handler: (memos: AiMemoDoc[]) => void): Unsubscribe {
        const q = query(
            collection(this.c.store, ROOM_COL, roomId, MEMO_COL),
            orderBy('created', 'desc'),
            limit(100)
        );
        return onSnapshot(q, (snap) => {
            handler(snap.docs.map(d => ({ ...d.data(), id: d.id } as AiMemoDoc)));
        });
    }

    /**
     * Subscribe to memos filtered by category
     */
    subscribeToMemosByCategory(roomId: string, category: string, handler: (memos: AiMemoDoc[]) => void): Unsubscribe {
        const q = query(
            collection(this.c.store, ROOM_COL, roomId, MEMO_COL),
            where('category', '==', category),
            orderBy('created', 'desc'),
            limit(100)
        );
        return onSnapshot(q, (snap) => {
            handler(snap.docs.map(d => ({ ...d.data(), id: d.id } as AiMemoDoc)));
        });
    }

    /**
     * Get a single memo
     */
    async getMemo(roomId: string, memoId: string): Promise<AiMemoDoc | null> {
        const d = await getDoc(doc(this.c.store, ROOM_COL, roomId, MEMO_COL, memoId));
        if (!d.exists()) return null;
        return { ...d.data(), id: d.id } as AiMemoDoc;
    }

    /**
     * Save (create or update) a memo
     */
    async saveMemo(roomId: string, userId: string, memo: Partial<AiMemoDoc>): Promise<string> {
        const memoId = memo.id || doc(collection(this.c.store, ROOM_COL, roomId, MEMO_COL)).id;
        const memoRef = doc(this.c.store, ROOM_COL, roomId, MEMO_COL, memoId);

        const data: any = {
            ...memo,
            id: memoId,
            roomId,
            userId,
            updated: serverTimestamp(),
        };

        // Only set created on new docs
        if (!memo.id) {
            data.created = serverTimestamp();
        }

        await setDoc(memoRef, data, { merge: true });
        return memoId;
    }

    /**
     * Update specific fields of a memo
     */
    async updateMemo(roomId: string, memoId: string, fields: Partial<AiMemoDoc>): Promise<void> {
        const memoRef = doc(this.c.store, ROOM_COL, roomId, MEMO_COL, memoId);
        await updateDoc(memoRef, { ...fields, updated: serverTimestamp() });
    }

    /**
     * Delete a memo
     */
    async deleteMemo(roomId: string, memoId: string): Promise<void> {
        await deleteDoc(doc(this.c.store, ROOM_COL, roomId, MEMO_COL, memoId));
    }

    // ============================== Minutes ==============================

    /**
     * Subscribe to room's meeting minutes (real-time, latest 50)
     */
    subscribeToMinutes(roomId: string, handler: (minutes: AiMinutesDoc[]) => void): Unsubscribe {
        const q = query(
            collection(this.c.store, ROOM_COL, roomId, MINUTES_COL),
            orderBy('created', 'desc'),
            limit(50)
        );
        return onSnapshot(q, (snap) => {
            handler(snap.docs.map(d => ({ ...d.data(), id: d.id } as AiMinutesDoc)));
        });
    }

    /**
     * Get a single minutes document
     */
    async getMinutes(roomId: string, minutesId: string): Promise<AiMinutesDoc | null> {
        const d = await getDoc(doc(this.c.store, ROOM_COL, roomId, MINUTES_COL, minutesId));
        if (!d.exists()) return null;
        return { ...d.data(), id: d.id } as AiMinutesDoc;
    }

    /**
     * Subscribe to a single minutes document (real-time)
     * Cloud STT 결과 등 비동기 업데이트를 자동 반영
     */
    subscribeToSingleMinutes(roomId: string, minutesId: string, handler: (doc: AiMinutesDoc | null) => void): Unsubscribe {
        const docRef = doc(this.c.store, ROOM_COL, roomId, MINUTES_COL, minutesId);
        return onSnapshot(docRef, (snap) => {
            if (!snap.exists()) return handler(null);
            handler({ ...snap.data(), id: snap.id } as AiMinutesDoc);
        });
    }

    /**
     * Save (create or update) minutes
     */
    async saveMinutes(roomId: string, userId: string, minutes: Partial<AiMinutesDoc>): Promise<string> {
        const minutesId = minutes.id || doc(collection(this.c.store, ROOM_COL, roomId, MINUTES_COL)).id;
        const minutesRef = doc(this.c.store, ROOM_COL, roomId, MINUTES_COL, minutesId);

        const data: any = {
            ...minutes,
            id: minutesId,
            roomId,
            userId,
            updated: serverTimestamp(),
        };

        if (!minutes.id) {
            data.created = serverTimestamp();
        }

        await setDoc(minutesRef, data, { merge: true });
        return minutesId;
    }

    /**
     * Update specific fields of minutes
     */
    async updateMinutes(roomId: string, minutesId: string, fields: Partial<AiMinutesDoc>): Promise<void> {
        const minutesRef = doc(this.c.store, ROOM_COL, roomId, MINUTES_COL, minutesId);
        await updateDoc(minutesRef, { ...fields, updated: serverTimestamp() });
    }

    /**
     * Delete minutes
     */
    async deleteMinutes(roomId: string, minutesId: string): Promise<void> {
        // Delete Firestore document
        await deleteDoc(doc(this.c.store, ROOM_COL, roomId, MINUTES_COL, minutesId));

        // Delete Storage audio file
        try {
            const storage = getStorage();
            const audioRef = ref(storage, `ai-minutes/${roomId}/${minutesId}/recording.webm`);
            await deleteObject(audioRef);
        } catch (e: any) {
            // Ignore if file doesn't exist (404)
            if (e?.code !== 'storage/object-not-found') {
                console.warn('[AiMemo] Failed to delete audio file:', e);
            }
        }
    }

    // ============================== Statistics ==============================

    /**
     * Get memo count for a channel
     */
    async getMemoCount(roomId: string): Promise<number> {
        const q = query(collection(this.c.store, ROOM_COL, roomId, MEMO_COL));
        const snap = await getDocs(q);
        return snap.size;
    }

    /**
     * Get minutes count for a channel
     */
    async getMinutesCount(roomId: string): Promise<number> {
        const q = query(collection(this.c.store, ROOM_COL, roomId, MINUTES_COL));
        const snap = await getDocs(q);
        return snap.size;
    }

    // ============================
    // Custom Categories
    // Stored in: rooms/{roomId}/ai-settings/categories
    // ============================

    /**
     * Load custom categories
     */
    async getCustomCategories(roomId: string): Promise<MemoCategory[]> {
        const ref = doc(this.c.store, ROOM_COL, roomId, 'ai-settings', 'categories');
        const snap = await getDoc(ref);
        if (!snap.exists()) return [];
        return snap.data().items || [];
    }

    /**
     * Save custom categories
     */
    async saveCustomCategories(roomId: string, categories: MemoCategory[]): Promise<void> {
        const ref = doc(this.c.store, ROOM_COL, roomId, 'ai-settings', 'categories');
        await setDoc(ref, { items: categories, updated: serverTimestamp() }, { merge: true });
    }
}
